import React, { useEffect, useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase.js';
import { BRAND_NAME } from '../../branding';
import { logAdminActivity } from '../../utils/activityLogger';
import { formatCreatedAt, formatDateTime, getCallStatus } from './labOnboardingShared';
import '../dash.css';

function LabOnboardingComp() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('All');

  useEffect(() => {
    const callsQuery = query(collection(db, 'scheduledCalls'), orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(callsQuery, (snapshot) => {
      const nextCalls = [];

      snapshot.forEach((docItem) => {
        nextCalls.push({ id: docItem.id, ...docItem.data() });
      });

      setCalls(nextCalls);
      setLoading(false);
    }, (error) => {
      console.error('Failed to load lab onboarding calls:', error);
      setCalls([]);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const serviceOptions = useMemo(() => (
    ['All', ...Array.from(new Set(calls.map((call) => call.help).filter(Boolean))).sort()]
  ), [calls]);

  const filteredCalls = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    return calls.filter((call) => {
      if (serviceFilter !== 'All' && call.help !== serviceFilter) {
        return false;
      }

      if (!queryText) return true;

      const haystack = [
        call.name,
        call.email,
        call.phone,
        call.country,
        call.help
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(queryText);
    });
  }, [calls, searchTerm, serviceFilter]);

  const openClient = (call) => {
    logAdminActivity({
      action: 'View Lab Client',
      details: `Opened lab client: ${call.name || 'client'}`,
      meta: {
        page: 'Lab Onboarding',
        scheduledCallId: call.id,
        clientName: call.name || '',
        clientEmail: call.email || ''
      }
    });

    navigate(`/view-lab-client/${call.id}`, {
      state: { myData: call.id }
    });
  };

  return (
    <div className="clientsComp applicationsPage labOnboardingPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">{BRAND_NAME}</p>
          <h4>Recent Clients</h4>
          <p className="appsSubtext">Open any booking to view the client, send reminders, and manage the meeting from one page.</p>
        </div>
      </div>

      <div className="appsFiltersCard labActionBar">
        <InputGroup className="appsSearch">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search by client name, email, phone, country, or service"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </InputGroup>

        <div className="appsFilterGroup">
          <Form.Select className="filterSelect" value={serviceFilter} onChange={(event) => setServiceFilter(event.target.value)}>
            {serviceOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Form.Select>
        </div>
      </div>

      <div className="appsTableCard labTableCard">
        <div className="appsTableHeader">
          <h6>Recent clients</h6>
          <span>{filteredCalls.length} visible requests</span>
        </div>
        <div className="appsTableWrap">
          {loading ? (
            <div className="incomeStateCard">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading onboarding requests...</span>
              </Spinner>
              <p>Loading client onboarding requests...</p>
            </div>
          ) : (
            <Table hover responsive className="appsTable labCallsTable">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  <th>Scheduled</th>
                  <th>Country</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredCalls.length === 0 && (
                  <tr>
                    <td colSpan="7" className="appsEmptyState">No client requests match your current filters.</td>
                  </tr>
                )}
                {filteredCalls.map((call) => {
                  const status = getCallStatus(call);
                  return (
                    <tr key={call.id}>
                      <td>
                        <div className="labClientCell">
                          <strong>{call.name || 'Client'}</strong>
                          <span>{call.email || 'No email provided'}</span>
                        </div>
                      </td>
                      <td>{call.help || 'Not specified'}</td>
                      <td>{formatDateTime(call.date, call.time)}</td>
                      <td>{call.country || 'N/A'}</td>
                      <td>{formatCreatedAt(call.timestamp)}</td>
                      <td>
                        <span className={`labStatusBadge labStatusBadge--${status.tone}`}>{status.label}</span>
                      </td>
                      <td>
                        <button type="button" className="appsViewBtn" onClick={() => openClient(call)}>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}

export default LabOnboardingComp;
